import React, { useState } from 'react';
import './RsiExtremesExitConditionsBar.css';

/**
 * Exit condition status for RSI Extremes trade groups
 */
export interface RsiExtremesExitConditions {
  currentPrice: number;
  currentRsi?: number;  // Current RSI value for overbought check
  stopLoss?: number;
  rsiOverboughtThreshold?: number;  // RSI level for primary exit (e.g., 70)
  profitTarget?: number;   // Extended target price (30% exit)
  trailingStopActivation?: number;
  trailingStopPrice?: number;
  timeRemainingHours?: number;
  isPartialExit: boolean; // Whether 70% has been sold
}

/**
 * Props for RsiExtremesExitConditionsBar component
 */
export interface RsiExtremesExitConditionsBarProps {
  /** Exit conditions data */
  exitConditions?: RsiExtremesExitConditions;
  /** Whether data is currently loading */
  isLoading?: boolean;
  /** Optional CSS class name */
  className?: string;
}

/**
 * RsiExtremesExitConditionsBar Component
 * 
 * Displays exit conditions for RSI Extremes trade groups:
 * - Stop-Loss: Price-based
 * - RSI Overbought (70% exit): RSI-based (not price)
 * - Profit Target (30% exit): Price-based
 */
const RsiExtremesExitConditionsBar: React.FC<RsiExtremesExitConditionsBarProps> = ({
  exitConditions,
  isLoading = false,
  className = '',
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  // Loading state: show skeleton
  if (isLoading) {
    return (
      <div className={`rsi-extremes-exit-conditions-bar ${className}`}>
        <div className="exit-targets-display loading">
          <div className="exit-target-card skeleton">
            <div className="skeleton-shimmer"></div>
          </div>
          <div className="exit-target-card skeleton">
            <div className="skeleton-shimmer"></div>
          </div>
          <div className="exit-target-card skeleton">
            <div className="skeleton-shimmer"></div>
          </div>
        </div>
      </div>
    );
  }

  // No data state
  if (!exitConditions) {
    return null;
  }

  const {
    currentPrice,
    currentRsi,
    rsiOverboughtThreshold = 70,
    profitTarget,
    isPartialExit
  } = exitConditions;

  // Calculate price proximity
  const calculatePriceProximity = (targetPrice: number | undefined): number => {
    if (!targetPrice || !currentPrice || currentPrice <= 0) return 0;
    return ((targetPrice - currentPrice) / currentPrice) * 100;
  };

  // Calculate RSI proximity (how far from overbought)
  const calculateRsiProximity = (): number => {
    if (currentRsi == null) return 0;
    return rsiOverboughtThreshold - currentRsi;
  };

  const profitTargetProximity = calculatePriceProximity(profitTarget);
  const rsiProximity = calculateRsiProximity();

  // Determine status based on proximity
  const getPriceStatus = (proximity: number): 'at-target' | 'near' | 'far' => {
    const absProximity = Math.abs(proximity);
    if (absProximity < 0.5) return 'at-target';
    if (absProximity < 2.0) return 'near';
    return 'far';
  };

  const getRsiStatus = (): 'at-target' | 'near' | 'far' => {
    if (currentRsi == null) return 'far';
    if (currentRsi >= rsiOverboughtThreshold) return 'at-target';
    if (rsiProximity <= 5) return 'near'; // Within 5 RSI points
    return 'far';
  };

  // Format currency helper
  const formatPrice = (price: number): string => {
    if (price < 1) {
      return `$${price.toFixed(4)}`;
    }
    return `$${price.toFixed(2)}`;
  };

  // Build conditions array
  type ExitCondition = {
    label: string;
    displayValue: string;
    proximity: string;
    status: 'at-target' | 'near' | 'far';
    critical: boolean;
    isRsiBased: boolean;
  };

  const conditions: ExitCondition[] = [];

  // NOTE: Stop Loss is now shown in the Price Position visualization,
  // so we don't duplicate it here

  // 1. RSI Overbought (70% exit) - only if not in partial exit, RSI-based
  if (!isPartialExit) {
    const rsiStatus = getRsiStatus();
    conditions.push({
      label: 'RSI Overbought (70% Exit)',
      displayValue: `RSI ≥ ${rsiOverboughtThreshold}`,
      proximity: currentRsi != null ? `Current: ${currentRsi.toFixed(1)}` : 'N/A',
      status: rsiStatus,
      critical: false,
      isRsiBased: true,
    });
  }

  // 2. Profit Target (30% exit or extended) - price-based
  if (profitTarget) {
    const proximity = profitTargetProximity;
    const label = isPartialExit ? 'Profit Target (30% Exit)' : 'Profit Target (Extended)';
    conditions.push({
      label,
      displayValue: formatPrice(profitTarget),
      proximity: `${proximity > 0 ? '+' : ''}${proximity.toFixed(2)}%`,
      status: getPriceStatus(proximity),
      critical: false,
      isRsiBased: false,
    });
  }

  // Calculate summary text
  const getSummaryText = (): string => {
    if (isPartialExit) {
      return `Partial exit complete. Waiting for profit target at ${profitTarget ? formatPrice(profitTarget) : 'N/A'}`;
    }

    // Check RSI status for primary exit
    const rsiStatus = getRsiStatus();
    if (rsiStatus === 'at-target') {
      return `RSI Overbought! Exit 70% triggered`;
    }
    if (rsiStatus === 'near' && currentRsi != null) {
      return `RSI approaching overbought (${currentRsi.toFixed(1)} → ${rsiOverboughtThreshold})`;
    }

    return 'Monitoring exit conditions';
  };

  return (
    <div className={`rsi-extremes-exit-conditions-bar ${className}`}>
      {/* Summary header */}
      <div className="exit-targets-summary">
        {getSummaryText()}
      </div>

      {/* Exit Targets Display */}
      <div className="exit-targets-display">
        {conditions.map((condition, index) => {
          const { label, displayValue, proximity, status, critical, isRsiBased } = condition;

          let cardClass = `exit-target-card ${status}`;
          if (critical) cardClass += ' critical';
          if (isRsiBased) cardClass += ' rsi-based';

          // Color based on status
          let color = '#A78BFA'; // platform purple
          if (critical) color = '#ef4444'; // critical (red)
          else if (status === 'at-target') color = '#10b981'; // at target (green)
          else if (status === 'near') color = '#f59e0b'; // near (amber)

          return (
            <div
              key={index}
              className={cardClass}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              role="status"
              tabIndex={0}
            >
              <div className="target-label">{label}</div>
              <div className="target-price" style={{ color }}>{displayValue}</div>
              <div className="target-distance">{proximity}</div>

              {/* Tooltip with full details */}
              {hoveredIndex === index && (
                <div className="exit-condition-tooltip" role="tooltip">
                  <div className="tooltip-header">{label}</div>
                  <div className="tooltip-detail">
                    {isRsiBased ? 'RSI Threshold' : 'Target Price'}: {displayValue}
                  </div>
                  <div className="tooltip-proximity">{proximity}</div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RsiExtremesExitConditionsBar;
